export interface ColumnResizeHandleProps {
  /** The width so far, on every pointer move — show it, don't save it. */
  onResize: (width: number) => void;
  /** The width the drag ended on — keep it; null when it didn't move (drop the preview). */
  onResizeEnd: (width: number | null) => void;
  /** Double-click: fit the column to its content again. */
  onReset: () => void;
}

/**
 * The grab strip on a header cell's right edge. Drag to size the column;
 * double-click to let it fit its content again. Put it inside a `<th>` with
 * `position: relative` — the drag measures that cell.
 */
export function ColumnResizeHandle({ onResize, onResizeEnd, onReset }: ColumnResizeHandleProps) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize · double-click to fit"
      className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize touch-none select-none hover:bg-blue-400/60"
      // Never a click on the header behind it (which would sort).
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset();
      }}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const handle = e.currentTarget;
        const cell = handle.parentElement;
        if (!cell) return;
        const startX = e.clientX;
        const startWidth = cell.getBoundingClientRect().width;
        let width = startWidth;
        handle.setPointerCapture(e.pointerId);
        const move = (ev: PointerEvent) => {
          width = startWidth + ev.clientX - startX;
          onResize(width);
        };
        const up = () => {
          handle.removeEventListener('pointermove', move);
          handle.removeEventListener('pointerup', up);
          handle.removeEventListener('pointercancel', up);
          onResizeEnd(Math.round(width) !== Math.round(startWidth) ? width : null);
        };
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', up);
        handle.addEventListener('pointercancel', up);
      }}
    />
  );
}
